import React, { useState, useEffect, useRef } from 'react'
import { BrManageContentButton } from '@bloomreach/react-sdk'
import { updateSubscriptionData } from '../../../components/EmailSubscriptionCenter/Landing/Helper/helper.js'
import { isLoggedIntoSubscriptionCenter } from '../../../global/authentication/authentication-manager.js'
import {
  getSubscriptionsByName,
  getSubscriptionsForUser,
} from '../../../components/EmailSubscriptionCenter/Data/SubscriptionCenterDataSource.js'
import CategorySubscriptionButton from '../../../components/EmailSubscriptionCenter/Modal/CategorySubscriptionButton.jsx'

import {
  get529SubHeaderDoc,
  getTabNames,
} from '../investmentProducts/helpers/InvestmentProductsHelper.js'
import DOMPurify from 'dompurify'
import { encodeForURL } from '../../../global/formatters/string-formatter.js'

import './ProductHeader.css'

export const ProductHeader = ({ page, component }) => {
  // Product
  const product = component?.getParameters().product

  // Tabs
  const [tabs, setTabs] = useState(null)
  const [selectedTabKey, setSelectedTabKey] = useState()

  // Header Links
  const headerLinksDocument = page.getContent(
    component?.getModels()?.headerLinks
  )
  const headerLinks = headerLinksDocument?.model?.data?.linkList?.links
  const headerLinksRef = useRef()

  // Subscription Button
  const [subscriptions, setSubscriptions] = useState()
  const [subscriptionProfile, setSubscriptionProfile] = useState()
  const subscribeTopic = component?.getParameters().subscribeTopic

  // 529 SubHeaderContent
  const [subHeaderContent529, setSubHeaderContent529] = useState()

  // Sticky References
  const productHeaderRef = useRef()
  const subnavRef = useRef()
  const subheaderRef = useRef()

  const stylesProductHeader = {
    top: document.getElementsByClassName('nav-container')[0].offsetHeight,
  }

  const stylesProductSubnav = {
    top:
      document.getElementsByClassName('nav-container')[0].offsetHeight +
      (document.getElementById('spaProductHeader')?.offsetHeight || 0),
  }

  useEffect(() => {
    getData()
    showStickyOnScroll()

    if (subscribeTopic) {
      getSubscribeTopic()
    }

    if (product === '529-plan') {
      get529SubHeaderContent()
    }
  }, [])

  useEffect(() => {
    updateDivIDs()
  }, [tabs, window?.location?.hash])

  const get529SubHeaderContent = async () => {
    const subHeaderContent =
      (await get529SubHeaderDoc())?._embedded?.documents[0] || {}
    setSubHeaderContent529(subHeaderContent)
  }

  const getSubscribeTopic = async () => {
    const categorySubscription = await getSubscriptionsByName(subscribeTopic)

    let subscriptionsData = []
    let subscriptionProfileData = {}

    if (isLoggedIntoSubscriptionCenter()) {
      if (categorySubscription.length > 0) {
        subscriptionProfileData = await getSubscriptionsForUser()
        subscriptionsData = updateSubscriptionData(
          subscriptionProfileData.value,
          {
            subscriptions: categorySubscription,
          }
        )
      }
    } else {
      subscriptionsData = categorySubscription
    }
    setSubscriptions(subscriptionsData)
    setSubscriptionProfile(subscriptionProfileData)
  }

  const getData = async () => {
    const tabData =
      (await getTabNames(product.replaceAll('-', '.')))?._embedded?.documents[0]
        ?.bundles?.default || []

    const tabDataSortedKeys = Object.keys(tabData).sort()
    // Hardcoded upper limit 8 tabs
    const tabDataSorted = tabDataSortedKeys.slice(0, 8).map((key) => {
      // Remove tab order denoter from key so div ID will be more readable
      // Keys must be lowercase, since hash value is casted to lowercase in changeTab
      const _key = encodeForURL(key)

      return {
        key: _key.replace(/.*\./, '').toLowerCase(),
        label: tabData[_key],
      }
    })
    setTabs(tabDataSorted)
    setSelectedTabKey(tabDataSorted[0]?.key || 0)
  }

  function updateDivIDs() {
    if (tabs) {
      if (tabs.length > 0) {
        tabs.map((tab, i) => {
          const element = document.getElementById(`tab-placeholder-${i + 1}`)
          element && element.setAttribute('id', `${product}-${tab.key}`)
        })

        // Removes unused <div>'s
        for (let i = Object.entries(tabs).length; i < 8; i++) {
          const element = document.getElementById(`tab-placeholder-${i + 1}`)
          element && element.remove()
        }

        // Set based on hash, ensure valid hash, non case-sensitive
        let setDefaultHash = true
        if (window.location.hash) {
          const hash = window.location.hash.replace('#', '').toLowerCase()
          tabs.map((tab) => {
            if (tab.key === hash) {
              changeTab(hash)
              setDefaultHash = false
              window.location.hash = window.location.hash.toLowerCase()
            }
          })
        }

        // If no hash found/invalid hash set to first tab
        if (setDefaultHash) {
          window.location.hash = tabs[0].key
          changeTab(tabs[0].key)
        }
      } else {
        // Rename only the first <div>
        const element = document.getElementById(`tab-placeholder-1`)
        element && element.setAttribute('id', `${product}`)
        // Removes unused <div>'s
        for (let i = 2; i <= 8; i++) {
          const element = document.getElementById(`tab-placeholder-${i}`)
          element && element.remove()
        }
      }
    }
  }

  function changeTab(key) {
    if (key !== selectedTabKey) {
      tabs.map((t) => {
        const element = document.getElementById(`${product}-${t.key}`)
        if (element) {
          t.key === key
            ? element.classList.remove('u-hidden')
            : element.classList.add('u-hidden')
        }
      })
      setSelectedTabKey(key)
      window.scrollTo(0, 0)
    }
  }

  const showStickyOnScroll = () => {
    // Product and Tab Containers - to adjust padding, preventing jerky page shifts
    // Subheader container - to determine when subnav should be sticky
    const productHeaderContainer = document.getElementById(
      'product-header-container'
    )
    const tabContainer = document.getElementById('tabs-container')

    // Keep header and title height defined outside event listener to
    // prevent flickering, used to set top padding for product header container
    // depending on scrolling up or down
    const titleHeight =
      document.getElementById('spaProductHeaderTitle')?.offsetHeight || 0

    // Header height is constant until 768, if less than 768 we use
    // the larger between title height and link container height
    const headerHeight = matchMedia('(min-width: 768px)').matches
      ? productHeaderRef.current.offsetHeight
      : headerLinksRef.current.offsetHeight > titleHeight
        ? headerLinksRef.current.offsetHeight
        : titleHeight

    // Scroll Direction logic
    let scrollPosition = 0
    let scrollingUp
    window.addEventListener('scroll', () => {
      const bodyScrollTop = document.body.getBoundingClientRect().top
      scrollingUp = bodyScrollTop > scrollPosition
      scrollPosition = bodyScrollTop

      // No null check in place for navContainer and mainHeader, should always exist
      const navContainer = document.getElementsByClassName('nav-container')[0]
      const mainHeader = document.getElementsByClassName('MainHeader')[0]

      // Screen sizes above 1024px shows nav container, sticky-bar and MainHeader
      // shown instead when below screen size is <= 1024px
      const productHeaderOffset = matchMedia('(min-width: 1025px)').matches
        ? navContainer.offsetHeight
        : (document.getElementsByClassName('sticky-bar')[0]?.offsetHeight ||
            0) + mainHeader.offsetHeight

      // Product header is sticky once bottom of nav menu is halfway past the header
      const stickPoint =
        subheaderRef.current.getBoundingClientRect().top - headerHeight / 2
      const navBottom = navContainer.getBoundingClientRect().bottom
      const mainHeaderBottom = mainHeader.getBoundingClientRect().bottom
      const isStickyHeader =
        stickPoint <
          (matchMedia('(min-width: 1025px)').matches
            ? navBottom
            : mainHeaderBottom) &&
        mainHeader.classList.contains('sticky-header') &&
        matchMedia('(min-width: 768px)').matches

      // Subnav is sticky when header meets bottom of subheader content
      const subHeaderContentBottom =
        subheaderRef.current.getBoundingClientRect().bottom
      const isStickySubnav =
        subHeaderContentBottom < productHeaderOffset + titleHeight

      // Sticky logic
      if (isStickyHeader) {
        productHeaderRef.current.classList.add('is-sticky-product-header')
        productHeaderRef.current.style.top = `${productHeaderOffset}px`
        // Add padding to header container to prevent page from jumping up
        productHeaderContainer.style.paddingTop = `${headerHeight}px`
        if (scrollingUp) {
          headerLinksRef.current.classList.remove('hide-links-container')
        } else {
          headerLinksRef.current.classList.add('hide-links-container')
        }
        // Sticky subnav logic
        if (isStickySubnav && subnavRef.current) {
          subnavRef.current.classList.add('is-sticky-product-subnav')
          // Add padding to tab container to prevent page from jumping up
          tabContainer.style.paddingTop = `${subnavRef.current.offsetHeight}px`
          // If scrolling up set subnav top to be the and nav height and full header height combined,
          // otherwise nav height and title height combined
          if (scrollingUp) {
            subnavRef.current.style.top = `${
              productHeaderOffset + headerHeight
            }px`
          } else {
            subnavRef.current.style.top = `${
              productHeaderOffset + titleHeight
            }px`
          }
        } else {
          subnavRef.current &&
            subnavRef.current.classList.remove('is-sticky-product-subnav')
          tabContainer.style.paddingTop = '0'
        }
      } else {
        productHeaderRef.current.classList.remove('is-sticky-product-header')
        subnavRef.current &&
          subnavRef.current.classList.remove('is-sticky-product-subnav')
        headerLinksRef.current.classList.remove('hide-links-container')
        productHeaderContainer.style.paddingTop = '0'
        tabContainer.style.paddingTop = '0'
      }
    })
  }

  let bannerHeading = product?.replaceAll('-', ' ')
  if (product === 'closed-end-funds') {
    bannerHeading = 'Listed Closed-End Funds'
  } else if (product === 'managed-accounts') {
    bannerHeading = 'Separately Managed Accounts'
  } else if (product === 'morningstar-funds') {
    bannerHeading = 'Morningstar 4- and 5-Star Rated Funds'
  }

  return (
    <div
      className={
        'u-borderColorNeutralLight u-borderTop0 u-borderLeft0 u-borderRight0 u-bgColorWhite'
      }
    >
      <div>
        <div
          id="spaProductHeader"
          className="u-flex u-sm-flexCol"
          ref={productHeaderRef}
          style={stylesProductHeader}
        >
          <div className="AbsoluteHeader-container u-bgColorBrandLight">
            <div className="u-sizeConstrained u-sm-paddingBottom2gu u-sm-paddingBottom0">
              <div className="u-flex u-sm-flexCol AbsoluteHeader-container">
                <div className="u-flex u-flexAlignItemsCenter u-md-flexAuto u-lg-flexAuto u-bgColorBrandLight u-sm-bgNone u-textReversed u-textBold InvestmentProducts-sm-fixedHeader">
                  <h1
                    id="spaProductHeaderTitle"
                    className="u-textColorWhite u-paddingVert4gu u-textCapitalize"
                  >
                    {bannerHeading}
                  </h1>
                </div>
                {headerLinks && (
                  <BrManageContentButton content={headerLinksDocument} />
                )}
                <div
                  id="fundHeader-linkSection"
                  className="u-flex u-flexCol u-flexJustifyCenter u-paddingLeft5gu u-sm-paddingLeft0 HeaderLinks-container u-sm-bgNone u-paddingVert3gu"
                  ref={headerLinksRef}
                >
                  {headerLinks &&
                    headerLinks.map((link, idx) => (
                      <div
                        key={idx}
                        className="u-flex u-flexAlignItemsCenter u-paddingVert2gu"
                      >
                        <a
                          href={link.link[0]?.string}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-analytics={`${product}-${link.text
                            .replaceAll(' ', '-')
                            .toLowerCase()}-header-link`}
                        >
                          <span className="Icon--material u-textReversed u-marginRight1gu u-textSizeMsrp1 u-posRelative">
                            {link.icon ? link.icon : 'article'}
                          </span>
                          <span className="u-textReversed HeaderLinks-link">
                            {link.text}
                          </span>
                        </a>
                      </div>
                    ))}
                  {subscriptions && subscriptions.length !== 0 && (
                    <div className="u-flex u-flexAlignItemsCenter u-sm-paddingTop2gu u-paddingTop0">
                      <CategorySubscriptionButton
                        subscriptionProfile={subscriptionProfile}
                        categorySubscription={subscriptions[0]}
                        buttonNotClickedText="Subscribe for the latest ETF market news and insights"
                        buttonClickedText="Subscribed"
                        productSubscribe={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="subHeaderContent" ref={subheaderRef}>
          <div id="is-mutual-fund-content-zone-5"></div>
          {product === '529-plan' && (
            <div
              className="u-sizeConstrained u-paddingTop5gu u-paddingBottom3gu"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(subHeaderContent529?.html?.content),
              }}
            />
          )}
        </div>
        {tabs && tabs.length !== 0 && (
          <ul
            id="spaProductSubnav"
            className="List List--inline List--selectable u-flexJustifyCenter u-borderBottom u-paddingTop3gu u-bgColorWhite"
            ref={subnavRef}
            style={stylesProductSubnav}
          >
            {tabs.map((tab) => (
              <li
                key={tab.key}
                className={`u-textSizeMsrp1 u-sm-paddingHoriz1gu u-marginLeft2gu u-textBold List-item u-sm-textSizeMsr u-sm-flex u-textCenter u-flexAlignItemsCenter u-sm-flexGrow1 u-lg-flexAlignItemsTop ${
                  tab.key === selectedTabKey
                    ? 'is-selected u-textColorBrandLight'
                    : ''
                }`}
              >
                <a
                  href={`#${tab.key}`}
                  onClick={() => changeTab(tab.key)}
                  data-analytics="simple-entry-component-body-subnav-tab-btn"
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
